import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play } from 'lucide-react'

type Fund = { id: string; name: string; ticker: string; source: string }

type PeerRow = {
  fund_id: string
  name?: string | null
  ticker?: string | null
  mandate?: string | null
  is_subject?: boolean
  frequency?: string
  n?: number
  start?: string; end?: string
  ann_return?: number | null
  ann_vol?: number | null
  sharpe?: number | null
  sortino?: number | null
  calmar?: number | null
  max_dd?: number | null
  rank_sharpe?: number; rank_sortino?: number; rank_calmar?: number
  rank_ann_return?: number; rank_max_dd?: number
  error?: string
}

type PeersResp = {
  run_id: number; fund_id: string
  peer_set: string | null
  results: PeerRow[]
}

type AlphaResp = {
  run_id: number
  peer_ticker: string
  n_obs: number
  beta: number | null
  alpha_weekly: number | null
  alpha_annualized: number | null
  t_alpha: number | null
  p_alpha: number | null
  alpha_significant: boolean
}

export default function RiskPeers() {
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState('')
  const [peerSet, setPeerSet] = useState('aqr_family')
  const [peerTicker, setPeerTicker] = useState('SPX Index')

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })
  const peerSetsQ = useQuery<Record<string, string[]>>({
    queryKey: ['risk-stats-peer-sets'],
    queryFn: () => api.get('/risk-stats/peer-sets').then(r => r.data),
  })
  const hfriQ = useQuery<Record<string, number>>({
    queryKey: ['risk-stats-hfri-benchmarks'],
    queryFn: () => api.get('/risk-stats/hfri-benchmarks').then(r => r.data),
  })

  const runPeers = useMutation<PeersResp, unknown>({
    mutationFn: () => api.post('/risk-stats/peers', {
      fund_id: fundId, peer_set: peerSet,
    }).then(r => r.data),
  })
  const runAlpha = useMutation<AlphaResp, unknown>({
    mutationFn: () => api.post('/risk-stats/alpha-vs-peer', {
      fund_id: fundId, peer_ticker: peerTicker,
    }).then(r => r.data),
  })

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data.filter(f =>
      !s || f.name?.toLowerCase().includes(s) || f.ticker?.toLowerCase().includes(s)
    ).slice(0, 300)
  }, [fundsQ.data, search])

  const pct = (v: number | null | undefined, d = 2) => v == null ? '—' : `${(v * 100).toFixed(d)}%`
  const num = (v: number | null | undefined, d = 2) => v == null ? '—' : v.toFixed(d)

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Peer comparison</h1>
        <p className="text-muted-foreground">
          Rank the subject fund against a curated peer set on annualised return, Sharpe, Sortino, Calmar, and max drawdown.
          Also regress the fund on a mandate proxy (e.g. MSCI World, SPX) to estimate beta and alpha vs that benchmark.
        </p>
      </div>

      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="Search fund..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input flex-1"
          />
          <select
            value={fundId}
            onChange={e => setFundId(e.target.value)}
            className="input w-full md:w-[28rem]"
          >
            <option value="">-- Select fund --</option>
            {filteredFunds.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({f.ticker}) [{f.source}]</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Peer set</span>
            <select value={peerSet} onChange={e => setPeerSet(e.target.value)} className="input">
              {Object.keys(peerSetsQ.data ?? { aqr_family: [] }).map(k => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={!fundId || runPeers.isPending}
            onClick={() => runPeers.mutate()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40"
          >
            {runPeers.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Rank peers
          </button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-muted-foreground">Alpha vs</span>
            <select value={peerTicker} onChange={e => setPeerTicker(e.target.value)} className="input">
              <optgroup label="Equity indices (weekly)">
                <option value="SPX Index">SPX Index</option>
                <option value="MXWO Index">MXWO Index (MSCI World)</option>
                <option value="M1WO Index">M1WO Index (MSCI World TR)</option>
                <option value="NKY Index">NKY Index</option>
                <option value="SX5E Index">SX5E Index</option>
              </optgroup>
              {hfriQ.data && Object.keys(hfriQ.data).length > 0 && (
                <optgroup label="HFRI indices (monthly)">
                  {Object.entries(hfriQ.data).map(([label, id]) => (
                    <option key={label} value={`hfri:${id}`}>{label}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              type="button"
              disabled={!fundId || runAlpha.isPending}
              onClick={() => runAlpha.mutate()}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-muted disabled:opacity-40"
            >
              {runAlpha.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Compute
            </button>
          </div>
        </div>
        {(runPeers.isError || runAlpha.isError) && (
          <div className="text-sm text-destructive">
            {String((runPeers.error as any)?.response?.data?.detail
              ?? (runAlpha.error as any)?.response?.data?.detail
              ?? runPeers.error ?? runAlpha.error)}
          </div>
        )}
      </div>

      {runAlpha.data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Metric label={`β vs ${runAlpha.data.peer_ticker}`} value={num(runAlpha.data.beta)} />
          <Metric label="α (ann.)"            value={pct(runAlpha.data.alpha_annualized)} />
          <Metric label="t-stat"              value={num(runAlpha.data.t_alpha)} />
          <Metric label="p-value"             value={runAlpha.data.p_alpha == null ? '—' : runAlpha.data.p_alpha.toFixed(3)} />
          <Metric label="Significant?"        value={runAlpha.data.alpha_significant ? 'yes' : 'no'} />
        </div>
      )}

      {runPeers.data && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Peer ranking ({runPeers.data.peer_set})</h2>
            <span className="text-xs text-muted-foreground">
              run_id <span className="font-mono">{runPeers.data.run_id}</span>
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Fund</th>
                  <th className="text-left py-2 px-3">Mandate</th>
                  <th className="text-right py-2 px-3">n</th>
                  <th className="text-right py-2 px-3">Ann. ret</th>
                  <th className="text-right py-2 px-3">Ann. vol</th>
                  <th className="text-right py-2 px-3">Sharpe</th>
                  <th className="text-right py-2 px-3">Sortino</th>
                  <th className="text-right py-2 px-3">Calmar</th>
                  <th className="text-right py-2 px-3">Max DD</th>
                </tr>
              </thead>
              <tbody>
                {runPeers.data.results.map(r => (
                  <tr
                    key={r.fund_id}
                    className={'border-b border-border/50 ' + (r.is_subject ? 'bg-primary/10 font-medium' : '')}
                  >
                    <td className="py-2 px-3">
                      <div className="text-xs">{r.name ?? r.fund_id}</div>
                      <div className="text-xs text-muted-foreground font-mono">{r.ticker ?? r.fund_id}</div>
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {r.mandate ? (
                        <span className="text-xs px-1.5 py-0.5 rounded border border-border bg-muted">{r.mandate}</span>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{r.n ?? '—'}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {pct(r.ann_return)}
                      <RankPill v={r.rank_ann_return} />
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{pct(r.ann_vol)}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {num(r.sharpe)}
                      <RankPill v={r.rank_sharpe} />
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {num(r.sortino)}
                      <RankPill v={r.rank_sortino} />
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {num(r.calmar)}
                      <RankPill v={r.rank_calmar} />
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {pct(r.max_dd)}
                      <RankPill v={r.rank_max_dd} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-muted-foreground mt-3">
            Rank badges: 1 = best in the selected peer set. Subject fund highlighted.
            {runPeers.data.results[0]?.frequency === 'monthly' && (
              <span> Stats computed on <span className="font-mono">monthly</span> returns (HFRI is monthly-native).</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold mt-1 font-mono">{value}</div>
    </div>
  )
}

function RankPill({ v }: { v?: number }) {
  if (v == null) return null
  const tint = v === 1 ? 'bg-green-500/15 text-green-700 dark:text-green-400'
             : v === 2 ? 'bg-muted text-muted-foreground'
             : 'bg-muted text-muted-foreground'
  return <span className={'ml-1 inline-block px-1.5 rounded text-[10px] align-middle ' + tint}>#{v}</span>
}
